/**
 * Time synchronization utility to track the difference between client and server time.
 * This helps identify clock drift issues that can affect timestamps and event ordering.
 *
 * Storage: clientServerTimeDiff is stored in serviceState and automatically synced to localStorage
 * via a Valtio subscriber in state.ts
 */

import FullStoryModule from '@modules/FullStoryModule';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { serviceState } from '../state';
import { ClientServerTimeDiff, TIME_DIFF_THRESHOLD_MS } from './types';
import { RtcsEvent } from '@servicetitan/telecom-rtcs-client';

/**
 * Calculate the time difference between client and server time
 * @param serverTimestamp ISO string timestamp from server event
 * @returns ClientServerTimeDiff object with the calculated difference
 */
export function calculateTimeDiff(
  serverTimestamp: string,
): ClientServerTimeDiff {
  const clientTime = Date.now();
  const serverTime = new Date(serverTimestamp).getTime();
  const diffMs = clientTime - serverTime;

  const description = formatTimeDiff(clientTime, serverTime);

  const clientServerTimeDiff: ClientServerTimeDiff = {
    diffMs,
    clientTime,
    serverTime,
    description,
  };

  return clientServerTimeDiff;
}

/**
 * Format time difference in a human-readable way
 * @param clientTime - Client timestamp in milliseconds
 * @param serverTime - Server timestamp in milliseconds
 */
function formatTimeDiff(clientTime: number, serverTime: number): string {
  const diffMs = clientTime - serverTime;
  const absDiff = Math.abs(diffMs);
  const seconds = Math.floor(absDiff / 1000); // Convert ms to seconds
  const minutes = Math.floor(seconds / 60); // Convert seconds to minutes
  const hours = Math.floor(minutes / 60); // Convert minutes to hours

  const direction = diffMs >= 0 ? 'ahead' : 'behind';
  const isOutOfSync = absDiff >= TIME_DIFF_THRESHOLD_MS;

  // If in sync, show simpler message
  if (!isOutOfSync) {
    return `In Sync (threshold: ${TIME_DIFF_THRESHOLD_MS}ms)`;
  }

  // Out of sync - show detailed breakdown
  const syncStatus = 'Out of Sync';

  // Less than 1 second: show milliseconds
  if (absDiff < 1000) {
    return `${syncStatus} (threshold: ${TIME_DIFF_THRESHOLD_MS}ms): Client is ${absDiff}ms ${direction} of server`;
  }
  // Less than 1 minute: show seconds only
  else if (seconds < 60) {
    return `${syncStatus} (threshold: ${TIME_DIFF_THRESHOLD_MS}ms): Client is ${seconds}s ${direction} of server`;
  }
  // Less than 1 hour: show minutes and remaining seconds
  else if (minutes < 60) {
    const remainingSeconds = seconds % 60; // Get seconds after full minutes
    return `${syncStatus} (threshold: ${TIME_DIFF_THRESHOLD_MS}ms): Client is ${minutes}m ${remainingSeconds}s ${direction} of server`;
  }
  // 1 hour or more: show hours and remaining minutes
  else {
    const remainingMinutes = minutes % 60; // Get minutes after full hours
    return `${syncStatus} (threshold: ${TIME_DIFF_THRESHOLD_MS}ms): Client is ${hours}h ${remainingMinutes}m ${direction} of server`;
  }
}

/**
 * Store time difference data in serviceState
 * localStorage sync happens automatically via Valtio subscriber
 */
export function storeTimeDiff(
  clientServerTimeDiff: ClientServerTimeDiff,
): void {
  serviceState.clientServerTimeDiff = clientServerTimeDiff;
}

/**
 * Retrieve stored time difference data from serviceState only
 * We only check serviceState since localStorage is automatically synced via Valtio
 */
export function getStoredTimeDiff(): ClientServerTimeDiff | null {
  try {
    // Only get from serviceState - this is the single source of truth
    return serviceState.clientServerTimeDiff ?? null;
  } catch (error) {
    const message = `Failed to retrieve time difference from serviceState: ${error}`;
    DiagnosticsModule.reportUserError({
      message,
      priority: 'direct',
    });
    return null;
  }
}

/**
 * Clear stored time difference data from serviceState
 * localStorage sync happens automatically via Valtio subscriber
 * Call this on logout to ensure fresh calculation on next login
 */
export function clearTimeDiff(): void {
  serviceState.clientServerTimeDiff = null;
}

/**
 * Check if time sync has already been calculated for this session
 */
export function isTimeSyncCalculated(): boolean {
  return getStoredTimeDiff() !== null;
}

/**
 * Calculate, store, log, and return time difference
 * Only calculates once per session - subsequent calls return the stored value
 */
export function processServerTimestamp(
  serverTimestamp?: string,
  context?: string,
  trackEvent?: (name: string, properties: Record<string, any>) => void,
): ClientServerTimeDiff | null {
  if (!serverTimestamp) {
    return null;
  }

  // Check if already calculated
  const existing = getStoredTimeDiff();
  if (existing) {
    return null; // Already calculated, skip
  }

  const clientServerTimeDiff = calculateTimeDiff(serverTimestamp);

  // Store in localStorage
  storeTimeDiff(clientServerTimeDiff);

  const isOutOfSync =
    Math.abs(clientServerTimeDiff.diffMs) >= TIME_DIFF_THRESHOLD_MS;

  logExistingTimeDiff({ context });

  // Track the event if out of sync and a tracking function was provided
  if (isOutOfSync && trackEvent) {
    trackEvent('Client Out of Sync with Server', {
      ...clientServerTimeDiff,
      eventType: context ?? 'unknown',
    });
  }

  return clientServerTimeDiff;
}

/**
 * Track time sync from RTCS event with timestamp
 * This should be called at the top level of the workspace event handling,
 * not inside individual event handlers.
 *
 * @param event - Any RTCS event with a timestamp property
 * @param eventType - The type of event for logging purposes
 */
export function trackTimeSyncFromRtcsEvent(
  event: RtcsEvent,
  eventType: string,
): void {
  try {
    // Check if already calculated
    const existing = getStoredTimeDiff();
    if (existing) {
      return; // Already calculated, skip
    }

    processServerTimestamp(event.timestamp, eventType, (name, properties) => {
      FullStoryModule.trackEvent(name, properties);
    });
  } catch (error) {
    // do not throw - just log
    const message = `[ClientServerTimeDiff] Error tracking time sync from RTCS event: ${error}`;
    DiagnosticsModule.reportUserError({
      message,
      priority: 'direct',
    });
  }
}

/**
 * Log existing client-server time diff if it exists
 */
export function logExistingTimeDiff({
  context,
}: { context?: string } = {}): void {
  const existing = getStoredTimeDiff();
  if (existing) {
    const contextPrefix = context ? `(${context}) ` : '';
    console.info(
      `[ClientServerTimeDiff] ${contextPrefix}${existing.description}`,
      JSON.stringify(existing),
    );
  }
}
